import { Fragment, useMemo, useState } from "react";
import clsx from "clsx";
import { DateTime } from "luxon";
import { useQuery } from "@tanstack/react-query";
import { ChevronDown, Loader2, TrendingUp } from "lucide-react";
import type { AirportConfig } from "@yb/core";
import { api, ApiError, type DayForecast, type ForecastFlight, type ForecastSlot } from "../lib/api";
import { airlineColor } from "../lib/format";
import { fillPct, fillTone, type FillBand } from "../lib/history";
import {
  ACCURACY_BANDS,
  LANE_BAR,
  LANE_TEXT,
  QUARTER,
  accuracyBand,
  accuracyTone,
  airlineOrder,
  laneBand,
  pressureStrip,
  pressureWindows,
  stackSlot,
  type Pressure,
} from "../lib/forecast-view";
import { AirlineMark } from "./AirlineMark";
import { FillScaleChips, HowToRead } from "./HowToRead";
import { Section } from "./Section";
import { ForecastReviewTab } from "./ForecastReview";
import { UploadLoads } from "./UploadLoads";

/**
 * How many passengers will be at security, and when.
 *
 * Written to be read in the order a duty manager thinks: what is the tightest moment
 * today, is it over what we can clear, and which flights put it there. Each section is
 * headed by the question it answers, with a line under it saying what the numbers are.
 * The workings — how many days are behind the figure, what each flight's number rests on,
 * how the forecast has scored against reality — are all still here, further down, because
 * a forecast nobody can weigh is a forecast nobody should act on. They are just no longer
 * the first thing in the way of the answer.
 */

const card = "rounded-xl border border-line p-4";
const tabClass = (on: boolean) => clsx("rounded-lg px-3 py-1.5 text-[12.5px] font-semibold", on ? "bg-panel-2 text-ink" : "text-ink-2 hover:text-ink");

/** Plain words for where a flight's passenger figure came from, best to worst. */
export const BASIS: Record<string, { label: string; long: string; tone: string }> = {
  booked: { label: "Booked", long: "The airline's own figure for this flight, from its load report", tone: "text-ok" },
  flight: {
    label: "This flight",
    long: "What this same flight number put through security on its past days here, averaged over at least two whose readings were complete; the row says how many",
    tone: "text-ok",
  },
  "airline-hour": {
    label: "Measured",
    long: "What this airline's flights at this hour have put through security before, averaged over at least three past flights whose readings were complete; the row says how many",
    tone: "text-ok",
  },
  airline: { label: "Airline average", long: "What this airline's flights usually put through, across the whole day", tone: "text-ink-2" },
  airport: { label: "Airport average", long: "What a flight here usually puts through; nothing specific to this airline yet", tone: "text-warn" },
  assumed: { label: "Assumed", long: "Nothing measured yet, so 80% of the aircraft's seats", tone: "text-bad" },
};

export function Forecast({ today, tz, config, canUpload = false }: { today: string; tz: string; config?: AirportConfig; canUpload?: boolean }) {
  const [date, setDate] = useState(today);
  // The review is every airline's passengers together, so it is the planners' and administrators'.
  const [tab, setTab] = useState<"day" | "review">("day");
  const q = useQuery({ queryKey: ["forecast", date], queryFn: () => api.forecast(date), retry: false, enabled: tab === "day" });
  const d = q.data;
  const notOn = q.error instanceof ApiError && q.error.status === 503;

  return (
    <div className="scroll-thin h-full overflow-auto p-4">
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <h1 className="mr-2 flex items-center gap-2 text-[18px] font-bold">
          <TrendingUp className="size-5 text-ink-3" /> Forecast
        </h1>
        {canUpload && (
          <>
            <button className={tabClass(tab === "day")} onClick={() => setTab("day")}>
              Day forecast
            </button>
            <button className={tabClass(tab === "review")} onClick={() => setTab("review")}>
              Forecast vs actual
            </button>
          </>
        )}
        {q.isFetching && <Loader2 className="size-4 animate-spin text-ink-3" />}
        {tab === "day" && <div className="ml-auto flex flex-wrap items-center gap-2 text-[12px]">
          {/* The airline's booked figures are the best input this page has, so the way in is here. */}
          {canUpload && <UploadLoads compact />}
          <label className="flex items-center gap-1">
            Day{" "}
            <input type="date" className="rounded-md border border-line bg-panel px-2 py-1.5 text-[12.5px]" value={date} onChange={(e) => setDate(e.target.value)} />
          </label>
        </div>}
      </div>

      {tab === "review" ? (
        <ForecastReviewTab today={today} tz={tz} />
      ) : notOn ? (
        <p className="text-[13px] text-ink-3">The forecast needs the security module, which is not running on this server.</p>
      ) : !d ? (
        <p className="text-[13px] text-ink-3">Working it out…</p>
      ) : (
        <Day d={d} tz={tz} today={today} date={date} config={config} />
      )}
    </div>
  );
}

export function Day({ d, tz, today, date, config }: { d: DayForecast; tz: string; today: string; date: string; config: AirportConfig | undefined }) {
  const hhmm = (ms: number) => DateTime.fromMillis(ms).setZone(tz).toFormat("HH:mm");
  /** What the lanes we have clear in one quarter hour. The line everything is read against. */
  const perQuarter = Math.max(1, Math.round((d.capacity.perLaneHour * d.capacity.lanes) / 4));
  const filled = useFilledSlots(d.slots);
  const windows = useMemo(() => pressureWindows(filled, d.capacity.lanes), [filled, d.capacity.lanes]);
  const over = windows.filter((w) => w.kind === "over");

  if (!d.slots.length) {
    return <p className="text-[13px] text-ink-3">No departures to forecast for this day.</p>;
  }

  const busiestTone = d.busiest ? LANE_TEXT[laneBand(d.busiest.lanesNeeded, d.capacity.lanes)] : undefined;
  const range = { first: filled[0]!.at, end: filled.at(-1)!.at + QUARTER };
  const lanes = d.capacity.lanes;

  return (
    <>
      <HowToRead id="forecast">
        <ForecastNotes />
      </HowToRead>

      <Section
        title="How busy will security be, and when?"
        caption={`The tightest quarter hour of the day, how many people reach security in it, and how many of the ${lanes} lanes that needs. The chart is every quarter hour; the dashed line is what the lanes clear.`}
      >
        <Answer d={d} windows={windows} perQuarter={perQuarter} hhmm={hhmm} />

        {d.comparison && <Delta c={d.comparison} today={date === today} />}

        <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
          <Tile label="Passengers expected" value={d.passengers.toLocaleString()} hint={`${d.flights.length} departures`} />
          {/* The two tiles about the peak carry the same colour as the bar they describe. */}
          <Tile label="Tightest quarter hour" value={d.busiest ? d.busiest.passengers.toLocaleString() : "—"} hint={d.busiest ? `people at ${hhmm(d.busiest.at)}` : undefined} tone={busiestTone} />
          <Tile label="Lanes needed then" value={d.busiest ? `${d.busiest.lanesNeeded} of ${lanes}` : "—"} tone={busiestTone} />
          <Tile
            label="Time over capacity"
            value={over.length ? `${over.reduce((n, w) => n + w.quarters, 0) * 15} min` : "None"}
            hint={over.length ? `${over.length} ${over.length === 1 ? "stretch" : "stretches"}` : "every quarter hour clears"}
            tone={over.length ? "text-bad" : "text-ok"}
          />
        </div>

        <Chart slots={filled} actual={d.actual} lanes={lanes} perQuarter={perQuarter} tz={tz} now={date === today ? Date.now() : null} config={config} />
      </Section>

      <Section title="Where does it get tight?" caption="Every stretch of the day where all the lanes are needed, or more than there are, with the flights putting people there. If you read nothing else, read this.">
        {windows.length ? (
          <Windows windows={windows} hhmm={hhmm} lanes={lanes} range={range} />
        ) : (
          <p className={clsx(card, "mb-4 text-[12.5px] text-ok")}>Nowhere. Every quarter hour clears with a lane to spare.</p>
        )}
      </Section>

      <Section
        title="Who is flying?"
        caption="Every departure that day: the passengers expected at security, how full that makes the aircraft, and how we know the figure. The words in that column are explained just below the table."
      >
        <Flights flights={d.flights} hhmm={hhmm} config={config} />
      </Section>

      <Section title="How much to trust this" caption="What each word in the How we know column means, most trustworthy first. Hover a word in the table for the same explanation.">
        <Basis />
      </Section>

      {d.accuracy.length > 0 && (
        <Section
          title="How right were we before?"
          caption="The first forecast made for each past day against the passengers who actually cleared security. A forecast revised once the day has flown is not a forecast, so only the first is scored."
        >
          <Accuracy d={d} />
        </Section>
      )}

      <Section title="How this is worked out" caption="What the whole forecast rests on: the days and flights measured at this airport, and what the checkpoint can clear.">
        <Workings d={d} perQuarter={perQuarter} />
      </Section>
    </>
  );
}

/** The Forecast page's How-to-read box: where the figures come from and what the colours mean. */
export function ForecastNotes() {
  return (
    <>
      <li>
        Passengers expected come from the airline's booked figure where one was uploaded, otherwise from what the same airline's flights at the same hour have put through
        security before. Nothing here is a vendor's estimate: security figures are counted from the checkpoint's boarding-pass scans (BPSS2).
      </li>
      <li>When they arrive is measured here too: each past flight's scans, in quarter hours counted back from its departure.</li>
      <li>
        The dashed line on the chart is what the lanes clear in a quarter hour; a bar above it is a queue forming. The strip under the chart (and the bars, with By airline
        switched off) is blue while the lanes clear, amber when every lane is needed, red when it is over. The solid line is who actually came through.
      </li>
      <li>Δ compares the forecast with what actually cleared security. Plus means more people were expected than came.</li>
      <li>
        How full is passengers against seats, on the same scale as the History page: <FillScaleChips />
      </li>
    </>
  );
}

/** The basis words explained once, in the order the forecast tries them, so the table above can use the word alone. */
export function Basis() {
  return (
    <div className={clsx(card, "mb-4")}>
      <dl className="grid gap-x-4 gap-y-1.5 text-[12.5px] sm:grid-cols-[max-content_1fr]">
        {Object.entries(BASIS).map(([key, b]) => (
          <Fragment key={key}>
            <dt className={clsx("font-semibold", b.tone)}>{b.label}</dt>
            <dd className="text-ink-2" data-basis={key}>
              {b.long}
            </dd>
          </Fragment>
        ))}
      </dl>
    </div>
  );
}

/** Quarter hours with nothing in them still belong on the chart, as gaps rather than absences. */
function useFilledSlots(slots: ForecastSlot[]): ForecastSlot[] {
  return useMemo(() => {
    if (!slots.length) return [];
    const by = new Map(slots.map((s) => [s.at, s]));
    const out: ForecastSlot[] = [];
    for (let at = slots[0]!.at; at <= slots.at(-1)!.at; at += QUARTER) {
      out.push(by.get(at) ?? { at, passengers: 0, lanesNeeded: 0, top: [] });
    }
    return out;
  }, [slots]);
}

/** The answer first, in a sentence, before any chart asks to be interpreted. */
function Answer({ d, windows, perQuarter, hhmm }: { d: DayForecast; windows: Pressure[]; perQuarter: number; hhmm: (ms: number) => string }) {
  const over = windows.filter((w) => w.kind === "over");
  const tight = windows.filter((w) => w.kind === "tight");
  const b = d.busiest;
  const tone = over.length ? "border-bad/50 bg-bad/5" : tight.length ? "border-warn/50 bg-warn/5" : "border-ok/40 bg-ok/5";
  const word = over.length ? "Over what the checkpoint clears" : tight.length ? "Tight" : "Comfortable";

  return (
    <div className={clsx("mb-4 rounded-xl border p-4", tone)}>
      <div className="text-[13px] font-semibold">{word}</div>
      <p className="mt-1 text-[13.5px] text-ink-2">
        {b ? (
          <>
            The tightest moment is <b className="text-ink">{hhmm(b.at)}</b>, with about <b className="text-ink">{b.passengers.toLocaleString()}</b> people arriving at
            security in that quarter hour. {d.capacity.lanes} {d.capacity.lanes === 1 ? "lane" : "lanes"} clear roughly {perQuarter.toLocaleString()} in the same time,
            so that needs{" "}
            <b className={over.length ? "text-bad" : tight.length ? "text-warn" : "text-ok"}>
              {b.lanesNeeded} of {d.capacity.lanes}
            </b>
            .
          </>
        ) : (
          "Nothing is expected at security today."
        )}
        {over.length > 0 && (
          <>
            {" "}
            {over.length === 1 ? "One stretch is" : `${over.length} stretches are`} past capacity:{" "}
            <b className="text-ink">{over.map((w) => `${hhmm(w.from)}–${hhmm(w.to)}`).join(", ")}</b>. Queues build through those and take time to clear afterwards.
          </>
        )}
        {over.length === 0 && tight.length > 0 && (
          <>
            {" "}
            Every lane is needed at <b className="text-ink">{tight.map((w) => `${hhmm(w.from)}–${hhmm(w.to)}`).join(", ")}</b>, so there is no room for a lane to go
            down.
          </>
        )}
        {over.length === 0 && tight.length === 0 && " No quarter hour needs every lane."}
      </p>
      {b && b.top.length > 0 && (
        <p className="mt-1.5 text-[12px] text-ink-3">
          Mostly {b.top.map((f) => `${f.flight} (${f.passengers})`).join(", ")}.
        </p>
      )}
    </div>
  );
}

/** Forecast against what has actually cleared security, in one line a duty manager can read. */
function Delta({ c, today }: { c: NonNullable<DayForecast["comparison"]>; today: boolean }) {
  const sign = c.delta > 0 ? "+" : "";
  const tone = c.deltaPct === null ? "text-ink-2" : accuracyTone(c.deltaPct).text;
  return (
    <div className="mb-4 flex flex-wrap items-baseline gap-x-3 gap-y-1 text-[13px]">
      <span className="font-semibold">{today ? "So far" : "This day"}</span>
      <span className="text-ink-2">
        forecast <b className="text-ink">{c.forecast.toLocaleString()}</b> against <b className="text-ink">{c.actual.toLocaleString()}</b> actually screened
      </span>
      <span className={clsx("tabular font-semibold", tone)}>
        Δ {sign}
        {c.delta.toLocaleString()}
        {c.deltaPct !== null && ` (${sign}${c.deltaPct}%)`}
      </span>
      <span className="text-[11.5px] text-ink-3">
        over {c.slots} quarter {c.slots === 1 ? "hour" : "hours"} · plus means we expected more than came
      </span>
    </div>
  );
}

/**
 * Passengers per quarter hour across the day, with the capacity line, hour gridlines and a
 * readout on hover. Built from positioned elements rather than a drawing library so it
 * reflows with the panel and stays legible at any width. What actually cleared security is
 * drawn over the bars as a line, so the gap between the two is the first thing seen.
 *
 * By default each bar is stacked by airline in the airlines' own colours, so "who is
 * putting people in the queue at 06:15" is read off the chart rather than hovered for;
 * switched off, the bars take the lane colours instead.
 */
export function Chart({
  slots,
  actual,
  lanes,
  perQuarter,
  tz,
  now,
  config,
}: {
  slots: ForecastSlot[];
  actual: DayForecast["actual"];
  lanes: number;
  perQuarter: number;
  tz: string;
  now: number | null;
  config: AirportConfig | undefined;
}) {
  const [at, setAt] = useState<number | null>(null);
  const [byAirline, setByAirline] = useState(true);
  const hovered = slots.find((s) => s.at === at) ?? null;
  const seen = new Map(actual.map((a) => [a.at, a.passengers]));
  const top = Math.max(...slots.map((s) => s.passengers), ...actual.map((a) => a.passengers), perQuarter, 1);
  const label = (ms: number) => DateTime.fromMillis(ms).setZone(tz).toFormat("HH:mm");
  const hour = (ms: number) => DateTime.fromMillis(ms).setZone(tz).toFormat("HH");
  const order = useMemo(() => airlineOrder(slots), [slots]);

  /** Y ticks at round numbers, so a height can be read off rather than guessed. */
  const step = top > 300 ? 100 : top > 120 ? 50 : top > 60 ? 25 : 10;
  const ticks: number[] = [];
  for (let v = step; v <= top; v += step) ticks.push(v);

  const first = slots[0]!.at;
  const span = slots.at(-1)!.at + QUARTER - first;
  const pos = (ms: number) => ((ms - first) / span) * 100;
  const nowIn = now != null && now >= first && now <= slots.at(-1)!.at + QUARTER;

  const shade = (s: ForecastSlot) => LANE_BAR[laneBand(s.lanesNeeded, lanes)];

  // The actual line, in the chart's own percentage space: x at the centre of the slot's
  // bar, y from the same scale as the bars. Only quarter hours that were recorded are
  // joined; a line falling to zero where nothing has been measured yet would be a lie.
  const half = (QUARTER / span) * 50;
  const points = slots
    .filter((s) => seen.has(s.at))
    .map((s) => ({ x: pos(s.at) + half, y: 100 - (seen.get(s.at)! / top) * 100 }));

  return (
    <div className={clsx(card, "mb-4")}>
      <div className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
        <div className="flex flex-wrap items-center gap-3">
          <div className="text-[13px] font-semibold">At security, quarter hour by quarter hour</div>
          <button
            className={clsx(
              "rounded-full border border-line px-2 py-0.5 text-[11px] font-semibold transition-colors hover:bg-panel-2/60",
              byAirline ? "bg-panel-2 text-ink" : "text-ink-3",
            )}
            aria-pressed={byAirline}
            title={byAirline ? "Colour the bars by how tight the lanes are instead" : "Colour the bars by airline"}
            onClick={() => setByAirline((v) => !v)}
          >
            By airline
          </button>
        </div>
        <div className="text-[11.5px] text-ink-3">
          Dashed line: what {lanes} {lanes === 1 ? "lane" : "lanes"} clear in a quarter hour ({perQuarter.toLocaleString()}), at {perLaneFrom(perQuarter, lanes)} per lane per
          hour
        </div>
      </div>

      <div className="flex gap-2">
        {/* Y axis */}
        <div className="relative h-44 w-8 shrink-0">
          {ticks.map((v) => (
            <div key={v} className="absolute right-0 -translate-y-1/2 text-[10px] text-ink-3" style={{ bottom: `${(v / top) * 100}%` }}>
              {v}
            </div>
          ))}
        </div>

        <div className="relative flex-1" onMouseLeave={() => setAt(null)}>
          <div className="relative h-44 border-b border-line">
            {/* Gridlines, behind the bars */}
            {ticks.map((v) => (
              <div key={v} className="absolute inset-x-0 border-t border-line/50" style={{ bottom: `${(v / top) * 100}%` }} />
            ))}
            {/* Hour marks */}
            {slots
              .filter((s) => DateTime.fromMillis(s.at).setZone(tz).minute === 0)
              .map((s) => (
                <div key={s.at} className="absolute inset-y-0 border-l border-line/60" style={{ left: `${pos(s.at)}%` }} />
              ))}
            {/* What the lanes clear */}
            <div className="absolute inset-x-0 border-t-2 border-dashed border-ink-3/70" style={{ bottom: `${(perQuarter / top) * 100}%` }} />
            {nowIn && <div className="absolute inset-y-0 z-10 border-l-2 border-info" style={{ left: `${pos(now!)}%` }} title="Now" />}
            {points.length > 0 && (
              <svg className="pointer-events-none absolute inset-0 z-10 h-full w-full text-ink" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden>
                {points.length > 1 && (
                  <polyline points={points.map((p) => `${p.x},${p.y}`).join(" ")} fill="none" stroke="currentColor" strokeWidth={2} vectorEffect="non-scaling-stroke" strokeLinejoin="round" />
                )}
                {/* A zero-length stroke with round caps is a dot that keeps its shape however the box is stretched. */}
                {points.map((p) => (
                  <polyline key={p.x} points={`${p.x},${p.y} ${p.x},${p.y}`} fill="none" stroke="currentColor" strokeWidth={6} strokeLinecap="round" vectorEffect="non-scaling-stroke" />
                ))}
              </svg>
            )}

            <div className="absolute inset-0 flex items-end gap-px">
              {slots.map((s) => (
                <button
                  key={s.at}
                  className="group flex h-full flex-1 cursor-default items-end"
                  onMouseEnter={() => setAt(s.at)}
                  onFocus={() => setAt(s.at)}
                  aria-label={`${label(s.at)}, ${s.passengers} passengers`}
                >
                  {byAirline ? (
                    <div className={clsx("relative h-full w-full transition-opacity duration-200", at === s.at ? "opacity-100" : "opacity-85")}>
                      {stackSlot(s, order, top).map((seg, i, all) => (
                        <div
                          key={seg.airline ?? "other"}
                          className={clsx("absolute inset-x-0 transition-[height,top] duration-200", i === all.length - 1 && "rounded-t", seg.airline == null && "bg-ink-3/40")}
                          style={{ top: `${seg.y}%`, height: `${seg.h}%`, minHeight: 1, background: seg.airline == null ? undefined : airlineColor(seg.airline, config) }}
                          data-airline={seg.airline ?? undefined}
                        />
                      ))}
                    </div>
                  ) : (
                    <div
                      className={clsx("w-full rounded-t transition-[height,opacity] duration-200", shade(s), at === s.at ? "opacity-100" : "opacity-85")}
                      style={{ height: `${(s.passengers / top) * 100}%`, minHeight: s.passengers ? 2 : 0 }}
                    />
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Hour labels */}
          <div className="relative mt-1 h-4">
            {slots
              .filter((s) => DateTime.fromMillis(s.at).setZone(tz).minute === 0 && Number(hour(s.at)) % 2 === 0)
              .map((s) => (
                <span key={s.at} className="absolute -translate-x-1/2 text-[10.5px] text-ink-3" style={{ left: `${pos(s.at)}%` }}>
                  {hour(s.at)}
                </span>
              ))}
          </div>

          {/* Lanes needed, as a staffing strip under the chart */}
          <div className="mt-2 flex gap-px">
            {slots.map((s) => (
              <div
                key={s.at}
                className={clsx("h-4 flex-1 text-center text-[9px] leading-4 font-semibold", s.passengers === 0 ? "bg-panel-2" : clsx(shade(s), "text-panel"))}
                title={`${label(s.at)} · ${s.lanesNeeded} of ${lanes} lanes`}
                onMouseEnter={() => setAt(s.at)}
              >
                {s.passengers === 0 ? "" : s.lanesNeeded}
              </div>
            ))}
          </div>
          <div className="mt-1 text-[10.5px] text-ink-3">Lanes needed, quarter hour by quarter hour</div>

          {/* Readout */}
          {hovered && (
            <div
              className="pointer-events-none absolute top-0 z-20 w-56 rounded-lg border border-line bg-panel p-2 text-[11.5px] shadow-lg"
              style={{ left: `min(max(0px, calc(${pos(hovered.at)}% - 112px)), calc(100% - 224px))` }}
            >
              <div className="font-semibold">
                {label(hovered.at)}–{label(hovered.at + QUARTER)}
              </div>
              <div className="text-ink-2">
                <b className="text-ink">{hovered.passengers.toLocaleString()}</b> people at security
              </div>
              <div className={LANE_TEXT[laneBand(hovered.lanesNeeded, lanes)]}>
                {hovered.lanesNeeded} of {lanes} {lanes === 1 ? "lane" : "lanes"} needed
              </div>
              {seen.has(hovered.at) && (
                <div className="mt-1 border-t border-line pt-1">
                  Actual <b>{seen.get(hovered.at)!.toLocaleString()}</b> ·{" "}
                  <span className={hovered.passengers - seen.get(hovered.at)! > 0 ? "text-warn" : "text-ok"}>
                    Δ {hovered.passengers - seen.get(hovered.at)! > 0 ? "+" : ""}
                    {(hovered.passengers - seen.get(hovered.at)!).toLocaleString()}
                  </span>
                </div>
              )}
              {hovered.top.length > 0 && (
                <div className="mt-1 flex flex-col gap-0.5 border-t border-line pt-1 text-ink-2">
                  {hovered.top.map((f) => (
                    <div key={f.flight} className="flex items-center gap-1.5">
                      <AirlineMark code={f.airline} config={config} />
                      <span className="font-semibold">{f.flight}</span>
                      <span className="tabular ml-auto">{f.passengers}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1.5 text-[11px] text-ink-3">
        {byAirline && order.length > 0 && (
          <>
            {order.map((code) => (
              <AirlineMark key={code} code={code} config={config} withName />
            ))}
            <Key className="bg-ink-3/40" label="smaller flights" />
            <span className="w-full sm:w-auto sm:border-l sm:border-line sm:pl-3">Strip below:</span>
          </>
        )}
        <Key className="bg-info" label="clears comfortably" />
        <Key className="bg-warn" label="every lane needed" />
        <Key className="bg-bad" label="more than the lanes clear" />
        {points.length > 0 && (
          <span className="flex items-center gap-1">
            <span className="inline-block h-0.5 w-4 rounded bg-ink" /> actually screened
          </span>
        )}
        {nowIn && <Key className="bg-info" label="the blue line is now" />}
      </div>
    </div>
  );
}

/** Per-lane per-hour, worked back so the dashed line can say where it came from. */
const perLaneFrom = (perQuarter: number, lanes: number) => Math.round((perQuarter * 4) / Math.max(1, lanes)).toLocaleString();

function Key({ className, label }: { className: string; label: string }) {
  return (
    <span className="flex items-center gap-1">
      <span className={clsx("inline-block size-2.5 rounded-sm", className)} /> {label}
    </span>
  );
}

/**
 * The stretches worth doing something about, with what is driving each one. A strip of
 * the day sits above the list so the shape of it — one long stretch or three short ones,
 * early or late — is seen before it is read.
 */
export function Windows({ windows, hhmm, lanes, range }: { windows: Pressure[]; hhmm: (ms: number) => string; lanes: number; range: { first: number; end: number } }) {
  if (!windows.length) return null;
  const strip = pressureStrip(windows, range.first, range.end);
  return (
    <div className={clsx(card, "mb-4")}>
      <div className="mb-2 text-[11.5px] text-ink-3">The day as a strip, first departure to last; a pill marks each tight stretch at its time.</div>
      <div className="mb-1 flex items-center gap-2 text-[10.5px] text-ink-3">
        <span className="tabular w-9 shrink-0">{hhmm(range.first)}</span>
        <div className="relative h-3 flex-1 overflow-hidden rounded bg-panel-2" role="img" aria-label={windows.map((w) => `${w.kind === "over" ? "over" : "all lanes"} ${hhmm(w.from)}–${hhmm(w.to)}`).join(", ")}>
          {strip.map((s) => (
            <div
              key={s.from}
              className={clsx("absolute inset-y-0 rounded-sm", s.kind === "over" ? "bg-bad" : "bg-warn")}
              style={{ left: `${s.x}%`, width: `${s.w}%` }}
              title={`${s.kind === "over" ? "Over" : "All lanes"} ${hhmm(s.from)}–${hhmm(s.to)}`}
            />
          ))}
        </div>
        <span className="tabular w-9 shrink-0 text-right">{hhmm(range.end)}</span>
      </div>
      <div className="mb-3 flex flex-wrap items-center gap-3 text-[11px] text-ink-3">
        <Key className="bg-warn" label="every lane needed" />
        <Key className="bg-bad" label="over what the lanes clear" />
      </div>
      <div className="flex flex-col gap-2">
        {windows.map((w) => (
          <div key={w.from} className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 text-[12.5px]">
            <span className={clsx("rounded px-1.5 py-0.5 text-[11px] font-semibold", w.kind === "over" ? "bg-bad/15 text-bad" : "bg-warn/15 text-warn")}>
              {w.kind === "over" ? "Over" : "All lanes"}
            </span>
            <b className="tabular">
              {hhmm(w.from)}–{hhmm(w.to)}
            </b>
            <span className="text-ink-3">
              {w.quarters * 15} min · peak {w.peak.toLocaleString()} · {w.lanesNeeded} of {lanes} needed
            </span>
            {w.flights.length > 0 && <span className="text-ink-2">Driven by {w.flights.slice(0, 4).join(", ")}</span>}
          </div>
        ))}
      </div>
    </div>
  );
}

/** The chip's wash behind the figure, band by band; the text colour comes from the scale itself. */
const FILL_WASH: Record<FillBand, string> = { none: "bg-panel-2", empty: "bg-bad/10", light: "bg-warn/15", good: "bg-ok/10", full: "bg-ok/15", over: "bg-bad/15" };

/**
 * How full the flight is expected to be, on the one fill scale the History page uses. A
 * figure of 150 means nothing on its own; 150 of 174 seats is a full flight, and 150 of
 * 76 is a number that cannot be right.
 */
export function FillChip({ passengers, seats }: { passengers: number; seats: number | null }) {
  const pct = fillPct(passengers, seats);
  const tone = fillTone(pct);
  if (pct == null) {
    return (
      <span className={clsx("rounded px-1.5 py-0.5 text-[10.5px]", FILL_WASH.none, tone.text)} title="The aircraft's seats are not known, so the fill cannot be worked out">
        no seats
      </span>
    );
  }
  return (
    <span className={clsx("tabular rounded px-1.5 py-0.5 text-[10.5px] font-semibold", FILL_WASH[tone.band], tone.text)} title={`${passengers} of ${seats} seats · ${tone.label}`}>
      {Math.round(pct)}%
    </span>
  );
}

/** Departures, with how much each one is trusted said in words rather than a code. */
export function Flights({ flights, hhmm, config }: { flights: ForecastFlight[]; hhmm: (ms: number) => string; config: AirportConfig | undefined }) {
  const most = Math.max(...flights.map((f) => f.passengers), 1);
  return (
    <div className={clsx(card, "mb-4")}>
      <table className="w-full text-[12.5px]">
        <thead>
          <tr className="border-b border-line text-left text-[11px] tracking-wide text-ink-3 uppercase">
            <th className="px-2 py-1.5">Flight</th>
            <th className="px-2 py-1.5" title="Scheduled departure time">
              Departs
            </th>
            <th className="px-2 py-1.5" title="People expected to reach security for this flight; the bar is against the biggest flight of the day">
              Passengers expected
            </th>
            <th className="px-2 py-1.5" title="Passengers expected against the aircraft's seats">
              How full
            </th>
            <th className="px-2 py-1.5" title="Where the passenger figure came from; explained under the table">
              How we know
            </th>
            <th className="px-2 py-1.5 text-right" title="Bags reported for this flight so far">
              Bags
            </th>
          </tr>
        </thead>
        <tbody>
          {flights.map((f) => {
            const b = BASIS[f.basis];
            // The "of N" beside the chip follows the chip's own rule for whether the seats are
            // usable, so a seat count of 0 reads "no seats" alone rather than "no seats of 0".
            const seatsKnown = fillPct(f.passengers, f.seats) != null;
            return (
              <tr key={`${f.flight}-${f.scheduled}`} className="border-b border-line">
                <td className="px-2 py-1.5 font-semibold">
                  <span className="flex items-center gap-1.5">
                    <AirlineMark code={f.airline} config={config} />
                    {f.flight}
                  </span>
                </td>
                <td className="tabular px-2 py-1.5 text-ink-2">{hhmm(f.scheduled)}</td>
                <td className="px-2 py-1.5">
                  <div className="flex items-center gap-2">
                    <span className="tabular w-8 shrink-0 text-right font-semibold">{f.passengers}</span>
                    <span className="h-1.5 max-w-40 flex-1 rounded-full bg-panel-2">
                      <span className="block h-full rounded-full bg-info transition-[width] duration-300" style={{ width: `${(f.passengers / most) * 100}%` }} />
                    </span>
                  </div>
                </td>
                <td className="px-2 py-1.5">
                  <span className="flex items-center gap-1.5">
                    <FillChip passengers={f.passengers} seats={f.seats} />
                    {seatsKnown && <span className="tabular text-[11px] text-ink-3">of {f.seats}</span>}
                  </span>
                </td>
                <td className="px-2 py-1.5">
                  <span className={clsx("text-[12px] font-semibold", b?.tone ?? "text-ink-3")} title={b?.long}>
                    {b?.label ?? f.basis}
                  </span>
                  {f.from > 0 && <span className="text-ink-3"> · from {f.from} past flights</span>}
                </td>
                <td className="tabular px-2 py-1.5 text-right text-ink-2">{f.bags ?? "—"}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/**
 * Forecast against what happened. The one number no vendor publishes about themselves.
 * Each day's pair of bars takes the colour of how close the two came, so a bad week is
 * seen as a run of red before any figure is read.
 */
export function Accuracy({ d }: { d: DayForecast }) {
  const [open, setOpen] = useState(false);
  if (!d.accuracy.length) return null;
  const scored = d.accuracy.filter((a) => a.deltaPct !== null);
  const typical = scored.length ? Math.round((scored.reduce((n, a) => n + Math.abs(a.deltaPct!), 0) / scored.length) * 10) / 10 : null;
  const top = Math.max(...d.accuracy.flatMap((a) => [a.forecast, a.actual]), 1);
  const days = [...d.accuracy].reverse();

  return (
    <div className={clsx(card, "mb-4")}>
      {typical !== null && (
        <div className="mb-3 text-[12.5px]">
          Typically out by <b className={accuracyTone(typical).text}>{typical}%</b> over {scored.length} {scored.length === 1 ? "day" : "days"}
        </div>
      )}

      <div className="flex h-28 items-end gap-2 overflow-x-auto">
        {days.map((a) => {
          const tone = accuracyTone(a.deltaPct);
          return (
            <div key={a.date} className="flex min-w-8 flex-1 flex-col items-center gap-1">
              <div className="flex h-full w-full items-end justify-center gap-0.5">
                <div
                  className={clsx("w-2.5 rounded-t opacity-45 transition-[height] duration-300", tone.bar)}
                  style={{ height: `${(a.forecast / top) * 100}%` }}
                  title={`Forecast ${a.forecast.toLocaleString()}`}
                  data-bar="forecast"
                />
                <div
                  className={clsx("w-2.5 rounded-t transition-[height] duration-300", tone.bar)}
                  style={{ height: `${(a.actual / top) * 100}%` }}
                  title={`Actual ${a.actual.toLocaleString()}`}
                  data-bar="actual"
                />
              </div>
              <span className={clsx("text-[9.5px]", tone.text)} title={a.date}>
                {a.deltaPct === null ? "—" : `${a.deltaPct > 0 ? "+" : ""}${a.deltaPct}%`}
              </span>
            </div>
          );
        })}
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-ink-3">
        <Key className="bg-ink opacity-45" label="forecast (lighter)" />
        <Key className="bg-ink" label="actually screened" />
        <span className="sm:border-l sm:border-line sm:pl-3">Coloured by how close:</span>
        {ACCURACY_BANDS.map((b) => (
          <Key key={b} className={accuracyBand(b).bar} label={accuracyBand(b).label} />
        ))}
      </div>

      <Disclose open={open} onToggle={() => setOpen((v) => !v)} label={`${open ? "Hide" : "Show"} the numbers`}>
        <table className="w-full max-w-lg text-[12.5px]">
          <thead>
            <tr className="border-b border-line text-left text-[11px] tracking-wide text-ink-3 uppercase">
              <th className="px-2 py-1.5">Day</th>
              <th className="px-2 py-1.5 text-right">Forecast</th>
              <th className="px-2 py-1.5 text-right">Actual</th>
              <th className="px-2 py-1.5 text-right">Out by</th>
            </tr>
          </thead>
          <tbody>
            {d.accuracy.map((a) => (
              <tr key={a.date} className="border-b border-line">
                <td className="px-2 py-1.5">{a.date}</td>
                <td className="tabular px-2 py-1.5 text-right">{a.forecast.toLocaleString()}</td>
                <td className="tabular px-2 py-1.5 text-right">{a.actual.toLocaleString()}</td>
                <td className={clsx("tabular px-2 py-1.5 text-right font-semibold", a.deltaPct === null ? "" : accuracyTone(a.deltaPct).text)}>
                  {a.deltaPct === null ? "—" : `${a.deltaPct > 0 ? "+" : ""}${a.deltaPct}%`}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Disclose>
    </div>
  );
}

/**
 * How the whole thing is worked out, and what it rests on. Kept off the top of the page
 * but never removed: anyone about to roster staff off this should be one click from
 * knowing how thin the evidence is.
 */
export function Workings({ d, perQuarter }: { d: DayForecast; perQuarter: number }) {
  const [open, setOpen] = useState(false);
  const thin = d.coverage.days < 14;
  const excluded = d.coverage.excluded ?? 0;
  return (
    <div className={clsx(card, "mb-4", thin ? "border-warn/50" : undefined)}>
      <div className="flex flex-wrap items-baseline justify-between gap-2 text-[12.5px]">
        <span>
          {thin ? (
            <>
              <b className="text-warn">Early days.</b> Built from {d.coverage.flights.toLocaleString()} flights over {d.coverage.days}{" "}
              {d.coverage.days === 1 ? "day" : "days"} measured here — not yet enough to trust a single flight's number.
            </>
          ) : (
            <>
              Measured here: <b>{d.coverage.flights.toLocaleString()} flights</b> over <b>{d.coverage.days} days</b>
              {d.coverage.since ? `, since ${d.coverage.since}` : ""}, covering {d.coverage.passengers.toLocaleString()} passengers through security.
            </>
          )}
          {excluded > 0 && (
            <span className="text-ink-3" title="BPSS2 returns at most 5,000 scans in a read, so a busy day's first flights can be read short. Those readings are kept but not learned from.">
              {" "}
              {excluded.toLocaleString()} past {excluded === 1 ? "flight" : "flights"} set aside as incomplete readings.
            </span>
          )}
        </span>
      </div>
      <Disclose open={open} onToggle={() => setOpen((v) => !v)} label={`${open ? "Hide" : "Show"} the method`}>
        <div className="max-w-3xl text-[12.5px] text-ink-2">
          <p className="mb-2">
            Two things are measured, and both come from this airport rather than a vendor's assumption. <b>How many</b>: what an airline's flights at this hour have
            actually put through security, averaged over the days behind them — not seats times a load factor. <b>When</b>: the show-up curve for that airline, sector
            and hour, in quarter hours counted back from departure.
          </p>
          <p className="mb-2">
            For each flight the narrowest thing with enough behind it wins, and the How we know column says which it was; the words are explained under How much to
            trust this, above.
          </p>
          <p className="mb-2">
            Capacity is {d.capacity.perLaneHour.toLocaleString()} passengers per lane per hour, {d.capacity.basis === "set" ? "set in Settings" : "assumed until the security manager gives us a figure"}
            . Across {d.capacity.lanes} {d.capacity.lanes === 1 ? "lane" : "lanes"} that is {d.capacity.checkpointPerHour.toLocaleString()} an hour, or{" "}
            {perQuarter.toLocaleString()} a quarter hour.
            {d.capacity.observedPeakPerHour != null && (
              <> The busiest quarter hour ever recorded here worked out at {d.capacity.observedPeakPerHour.toLocaleString()} an hour across the checkpoint.</>
            )}
          </p>
          {d.capacity.note && <p className="text-ink-3">{d.capacity.note}</p>}
        </div>
      </Disclose>
    </div>
  );
}

function Disclose({ open, onToggle, label, children }: { open: boolean; onToggle: () => void; label: string; children: React.ReactNode }) {
  return (
    <>
      <button className="mt-2 flex items-center gap-1 text-[12px] font-semibold text-ink-2 hover:text-ink" onClick={onToggle}>
        <ChevronDown className={clsx("size-3.5 transition-transform", open && "rotate-180")} /> {label}
      </button>
      {open && <div className="mt-3">{children}</div>}
    </>
  );
}

function Tile({ label, value, hint, tone }: { label: string; value: string; hint?: string; tone?: string }) {
  return (
    <div className={card}>
      <div className="text-[11px] tracking-wide text-ink-3 uppercase">{label}</div>
      <div className={clsx("tabular text-[20px] font-bold", tone)}>{value}</div>
      {hint && <div className="text-[11.5px] text-ink-3">{hint}</div>}
    </div>
  );
}
